import { useTranslation } from "react-i18next"
import { useBoardMutations } from "../use-board-mutations"
import { MoveCardData } from "../../api"
import { useConfirmDialog } from "../../../components/ConfirmDialog"
import { DropResult, OnDragUpdateResponder } from "react-beautiful-dnd"
import { ensureSubTasks, prefetchSubTasks } from "./subtask-service"
import { resolveMoveIntent, requiresConfirmation } from "./move-intent"
import { useTaskMutations } from "../../../tasks/hooks/use-task-mutations"
import { Status } from "../../../types/common"
import { BoardViewModelV2 } from "../../api/v2/board-view-model"
import { useLazyGetBoardV2Query } from "../../api/v2"

export const useBoard = (board: BoardViewModelV2) => {
  const { addTaskCard, addTodoCard, moveTaskCard, moveTodoCard, updateColumnName } = useBoardMutations()
  const taskActions = useTaskMutations()
  const { t } = useTranslation()
  const [refetchBoard] = useLazyGetBoardV2Query()

  const { confirm: confirmMoveToCompleted } = useConfirmDialog({
    onConfirm: async (moveCardData: MoveCardData) => {
      await taskActions.updateStatus(moveCardData.cardId, {
        status: Status.COMPLETED,
        subStatusId: null,
        updateSubtasks: true,
      })
      refetchBoard({ id: board.id, type: board.type })
    },
    primaryActionButtonLabel: t("boardConfirmDialog.primaryActionButtonLabel"),
    secondaryActionButtonLabel: t("boardConfirmDialog.secondaryActionButtonLabel"),
    title: t("boardConfirmDialog.titleLabel"),
    text: t("boardConfirmDialog.moveToCompleteTextLabel"),
  })

  const { confirm: confirmMoveToNotStarted } = useConfirmDialog({
    onConfirm: async (moveCardData: MoveCardData) => {
      await taskActions.updateStatus(moveCardData.cardId, {
        status: Status.NOT_STARTED,
        subStatusId: null,
        updateSubtasks: true,
      })
      refetchBoard({ id: board.id, type: board.type })
    },
    primaryActionButtonLabel: t("boardConfirmDialog.primaryActionButtonLabel"),
    secondaryActionButtonLabel: t("boardConfirmDialog.secondaryActionButtonLabel"),
    title: t("boardConfirmDialog.titleLabel"),
    text: t("boardConfirmDialog.moveToNotStartedTextLabel"),
  })

  const handleDragUpdate: OnDragUpdateResponder = (drag) => {
    const moveIntent = resolveMoveIntent(board, drag)
    if (moveIntent?.cardType === "task" && moveIntent?.mayRequireConfirmation) {
      prefetchSubTasks(drag.draggableId!)
    }
  }

  const handleDragEnd = async ({ source, destination, draggableId }: DropResult) => {
    const moveIntent = resolveMoveIntent(board, { source, destination, draggableId })
    if (!moveIntent) return

    if (moveIntent?.cardType === "todo") {
      moveTodoCard(moveIntent)
      return
    }

    if (moveIntent?.cardType === "task") {
      moveTaskCard(moveIntent)

      if (moveIntent.mayRequireConfirmation) {
        const { confirmationReason } = moveIntent
        const subTasks = await ensureSubTasks(draggableId)

        const mustConfirm = requiresConfirmation(draggableId, confirmationReason, subTasks)
        if (mustConfirm) {
          if (confirmationReason === "movingTaskToCompleted") return confirmMoveToCompleted(moveIntent)
          if (confirmationReason === "movingTaskToNotStarted") return confirmMoveToNotStarted(moveIntent)
        }
      }
    }
  }

  return {
    handleDragUpdate,
    handleDragEnd,
    handleAddTask: addTaskCard,
    handleAddTodo: addTodoCard,
    handleUpdateColumnName: updateColumnName,
  }
}
